import React, { useCallback, useEffect, useRef, useState } from "react";
import { Table as AntdTable } from "antd";
import { useTranslation } from "react-i18next";
import uniqueId from "lodash/uniqueId";
import classNames from "classnames";
import { connect } from "react-redux";
import isBoolean from "lodash/isBoolean";
import { useHistory, useLocation } from "react-router-dom";
import { useLoggedUser } from "../../utils/hooks/useLoggedUser";
import Preloader from "../Preloader";
import { applyColumnsFilterSort, translateColumnsTitles } from "./methods";
import TableFiltersActions from "./TableFiltersActions";
import { useTableFullHeight } from "./useTableFullHeight";
import TableService from "./TableService";
import useTableFetch from "./useTableFetch";

const tableGeneratedName = uniqueId("table_unique_key_name_");
const SmartTable = (props) => {
  const {
    route,
    tableService = new TableService(),
    disableSelectionRow,
    tableLocalStorageKeyName,
    name = tableGeneratedName,
    externalFilters,
    onGetData,
    columns = [],
    exportProps = {},
    newItemProps = {},
    tableState = {},
    massDeleteProps = {},
    scroll = {},
    transformer = (data) => data,
    transformerParams = (params) => params,
    containerClassName,
    onSelectRows,
    pagination,
    externalActions,
    clearFiltersActions,
    customHandleTableChange,
    staticData,
    rowsPerPage,
    footer,
    onError,
    emptyText = "container.general_words.no_data_message",
    ...rest
  } = props;

  const containerRef = useRef(null);
  const { height: tableHeight, pageSize: dynamicPageSize } = useTableFullHeight(containerRef);
  const location = useLocation();
  const history = useHistory();
  const loggedUser = useLoggedUser();
  const itemsPerPage = rowsPerPage || loggedUser?.company?.items_per_page;
  const [tableStateParams, setTableStateParams] = useState(
    tableService.setPerPage(itemsPerPage, true).setDefaultPerPage(itemsPerPage).getFilters()
  );
  const [selectedRows, setSelectedRows] = useState({ rows: [], keys: [] });
  const { t } = useTranslation();

  const { data, loading, initialized, search, setSearch, getTableData, debounceGetData } = useTableFetch({
    route,
    name,
    tableService,
    tableLocalStorageKeyName,
    tableState,
    staticData,
    transformer,
    transformerParams,
    onGetData,
    onError,
    onFetchStart: () => {
      setSelectedRows({ keys: [], rows: [] });
      setSearch(tableService.getSearchValue());
    },
    onFetchSuccess: () => {
      setTableStateParams(tableService.getFilters());
    }
  });

  const handleTableChange = (pagination, filters, sorter) => {
    if (customHandleTableChange) {
      tableService.setPerPage(tableStateParams.per_page);
      customHandleTableChange({ pagination, filters, sorter, tableService, getTableData, columns });
      return;
    }

    localStorage.setItem("smartTableLastPage", pagination.current);
    tableService.setPage(pagination.current);
    tableService.setPerPage(pagination.pageSize);
    tableService.removeAllOrderBy().manageOrderBy({ column: sorter.field, type: sorter.order });
    tableService.manageColumnFilters(filters, columns);
    getTableData(tableService.getFilters());
  };

  const handleSearchChange = useCallback(
    (e) => {
      history.replace({ search: "" });
      tableService.setSearchValue(e.target.value);
      tableService.setPage(1);
      setSearch(e.target.value);
      localStorage.setItem("smartTableLastSearch", e.target.value);
      debounceGetData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debounceGetData, tableService]
  );

  // General search functionality (URL-based)
  useEffect(() => {
    if (location.state?.generalSearch && location.search) {
      const searchValue = location.search.split("?search=")[1] || "";

      if (searchValue !== tableService.getSearchValue()) {
        tableService.setSearchValue(searchValue);
        tableService.setPage(1);
        getTableData(tableService.getFilters());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Dynamic page size from table height calculation
  useEffect(() => {
    if (pagination === false) return;
    if (dynamicPageSize && dynamicPageSize < tableStateParams.per_page) {
      tableService.setPerPage(dynamicPageSize);
      getTableData(tableService.getFilters());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicPageSize]);

  const searchColumns = tableService.getSearchColumns();
  const translatedColumns = translateColumnsTitles(columns, t);

  return (
    <div
      ref={containerRef}
      className={classNames("outer-table-container", containerClassName, {
        "global-search": searchColumns?.length > 0,
        loading
      })}
      style={{ minHeight: "100%" }}
    >
      <TableFiltersActions
        newItemProps={newItemProps}
        exportProps={exportProps}
        tableService={tableService}
        columns={translatedColumns}
        search={search}
        searchColumns={searchColumns}
        handleSearchChange={handleSearchChange}
        selectedRows={selectedRows.rows}
        massDeleteProps={massDeleteProps}
        tableName={name}
        externalFilters={externalFilters}
        externalActions={externalActions}
        clearFiltersActions={clearFiltersActions}
      />
      {initialized ? (
        <AntdTable
          showSorterTooltip={false}
          scroll={
            isBoolean(scroll)
              ? scroll
              : { x: scroll.x || 1300, y: pagination === false ? scroll.y : scroll.y || tableHeight }
          }
          tableLayout="auto"
          rowKey={(row = {}) => row.id || uniqueId("_table_unique_id_")}
          columns={applyColumnsFilterSort(translatedColumns, tableService)}
          dataSource={data}
          locale={{ emptyText: loading ? " " : t(emptyText) }}
          pagination={
            isBoolean(pagination)
              ? pagination
              : {
                  current: tableStateParams.page,
                  defaultPageSize: tableStateParams.defaultPageSize,
                  pageSize: tableStateParams.per_page,
                  total: tableStateParams.total,
                  ...pagination
                }
          }
          loading={{ spinning: loading, indicator: <Preloader /> }}
          onChange={handleTableChange}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedRows.keys,
            onChange: (selectedRowKeys, selectedRows) => {
              setSelectedRows({ rows: selectedRows, keys: selectedRowKeys });
              onSelectRows?.({ rows: selectedRows, keys: selectedRowKeys });
            },
            getCheckboxProps: (record) => ({
              disabled: disableSelectionRow ? disableSelectionRow(record) : false
            })
          }}
          {...rest}
        />
      ) : (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <Preloader />
        </div>
      )}
      {!!footer && footer}
    </div>
  );
};

const mapStateToProps = (state, { name = tableGeneratedName }) => {
  const tableState = state.table ? state.table[name] || {} : {};

  return {
    tableState
  };
};

export default connect(mapStateToProps)(SmartTable);
